import { App } from './App';
import { Component, ReactNode } from 'react';
import { ErrorResponse } from './ErrorManager';
import { useErrorManager } from './ErrorManager';
import Notification from 'components/atoms/Notification/Notification';

export type TranslateError<TApp extends App<any, any>> = (
  app: TApp,
  error: string,
) => string;

type ErrorBoundaryProps = {
  children?: ReactNode;
  app: App<any, any>;
};

type GlobalErrorBoundaryProps = {
  children?: ReactNode;
  error?: string | ErrorResponse;
  setError: (error?: string) => void;
  app: App<any, any>;
};

class ErrorHandlerComponent extends Component<GlobalErrorBoundaryProps> {
  constructor(props: GlobalErrorBoundaryProps) {
    super(props);

    this.onClose = this.onClose.bind(this);
  }
  static getDerivedStateFromError(error: any) {
    // Update state so the next render will show the fallback UI.
    return {};
  }

  componentDidCatch(error: ErrorResponse, info: any) {
    const { setError } = this.props;
    const message: string = this.getMessageFromError(error);
    setError(message);
  }

  onClose() {
    const { error, setError } = this.props;
    if (error) setError();
  }

  checkMessageExist = (messages: string[]) => {
    const { app } = this.props;

    let result;
    messages.reverse().forEach((message) => {
      const checkingMessage = app.translateError(app, message);

      const translationFound =
        !checkingMessage.includes('{') &&
        !checkingMessage.includes('validation.') &&
        !(!checkingMessage.includes(' ') && checkingMessage.includes('_'));
      if (translationFound) {
        result = checkingMessage;
      }
    });
    return result;
  };

  getMessageFromError = (error: ErrorResponse | string | undefined) => {
    const { app } = this.props;

    let messageNotification;
    // Error is an string when the parent has specify an error message according external conditions that only the parent knows
    if (typeof error === 'string') {
      messageNotification = this.checkMessageExist([error]);
    } else if (error) {
      (error.fieldErrors || error.errors)?.forEach((errorTemplate) => {
        // Checking if there is an error message for the template and, if it's possible, taking in mind the property
        const possibleMessages = [
          `validation.${errorTemplate?.messageTemplate}.${errorTemplate?.property}`,
          `${errorTemplate?.messageTemplate}`,
        ];
        messageNotification = this.checkMessageExist(possibleMessages);

        // In case the parent doesn't have an specific message to show... checking message according the status code from the response
        if (!messageNotification) {
          messageNotification = this.checkMessageExist([`${error?.status}`]);
        }
      });
    }

    const timeoutError = typeof error !== 'string' && error?.timeout;

    if (error && timeoutError !== 0) {
      setTimeout(() => {
        this.onClose();
      }, timeoutError || 3500);
    }

    // Conditional to avoid the empty error for corner cases that we didn't contemplate
    if (!messageNotification)
      messageNotification = app.translateError(app, 'default');

    return messageNotification;
  };

  render() {
    const { children, error } = this.props;

    const messageNotification = this.getMessageFromError(error);

    let notification = null;

    if (error) {
      notification = (
        <Notification
          onClose={this.onClose}
          text={messageNotification}
          type="error"
        />
      );
    }

    return (
      <>
        {notification}
        {children}
      </>
    );
  }
}

export const ErrorBoundary = (props: ErrorBoundaryProps) => {
  const { errorResponse, setError } = useErrorManager();

  return (
    <ErrorHandlerComponent
      {...props}
      error={errorResponse}
      setError={setError}
    />
  );
};
